import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../../../context/Toast';
import {
  useUpdateExpenseStatusMutation,
  ExpenseStatus,
  UpdateExpenseStatusInput,
} from '../../../../__generated__/graphql';

type UseExpenseSubmitProps = {
  expenseId: string | null | undefined;
  onSave: () => void;
  successMessage: string;
  errorMessage: string;
  validateBeforeSubmit?: () => Promise<boolean> | boolean;
  saveBeforeSubmit?: () => Promise<void>;
};

export const useExpenseSubmit = ({
  expenseId,
  onSave,
  successMessage,
  errorMessage,
  validateBeforeSubmit,
  saveBeforeSubmit,
}: UseExpenseSubmitProps) => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useToasts();
  const [updateExpenseStatusMutation] = useUpdateExpenseStatusMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitExpense = useCallback(async () => {
    if (!expenseId) {
      addError(t('expenses.expenseSaveModal.expenseIdRequired'));
      return;
    }

    if (saveBeforeSubmit) {
      await saveBeforeSubmit();
    }

    if (validateBeforeSubmit) {
      const isValid = await Promise.resolve(validateBeforeSubmit());
      if (!isValid) {
        addError(t('expenses.createEditModal.pleaseFillInAllRequiredData'));
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const result = await updateExpenseStatusMutation({
        variables: {
          input: {
            id: expenseId,
            status: ExpenseStatus.Pending,
            declineReason: null,
          } as UpdateExpenseStatusInput,
        },
      });

      if (result.data?.expenses?.updateExpenseStatus?.record) {
        addSuccess(successMessage);
        onSave();
      } else {
        addError(errorMessage);
      }
    } catch (err) {
      console.error('Error submitting expense:', err);
      addError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    expenseId,
    updateExpenseStatusMutation,
    addSuccess,
    addError,
    successMessage,
    errorMessage,
    onSave,
    t,
    validateBeforeSubmit,
    saveBeforeSubmit,
  ]);

  return {
    submitExpense,
    isSubmitting,
  };
};
